<script setup lang="ts">
import { ref, useTemplateRef } from "vue";

import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { eventTarget } from "@/utils/dom/dom";

const props = defineProps<{ pending?: boolean }>();
const emit = defineEmits<{ submit: [text: string]; stop: [] }>();

const text = ref("");
const inputRef = useTemplateRef("inputEl");

function handleEnter(event: KeyboardEvent) {
  if (event.shiftKey || event.isComposing) return;
  event.preventDefault();
  submit();
}

function submit() {
  const message = text.value.trim();
  if (!message || props.pending) return;
  text.value = "";
  inputRef.value!.style.height = "auto";
  emit("submit", message);
}

function sendOrStop() {
  if (props.pending) {
    emit("stop");
  } else {
    submit();
  }
}

function autogrow(event: Event) {
  const el = event.target as HTMLTextAreaElement;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
}

function focusInput(event: MouseEvent) {
  if (eventTarget(event).closest("button")) return;
  inputRef.value!.focus();
}
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions-->
  <div class="composer" @click="focusInput">
    <label class="input-area">
      <textarea
        ref="inputEl"
        v-model="text"
        v-autofocus
        rows="1"
        :placeholder="$t('sightline.inputPlaceholder')"
        :aria-label="$t('sightline.inputPlaceholder')"
        @keydown.stop
        @keydown.enter="handleEnter"
        @pointerdown.stop
        @input="autogrow"
      />
    </label>
    <!-- One element for both roles: swapping buttons would drop keyboard
         focus when the pending state flips. -->
    <div class="bottom-row">
      <button
        class="send"
        :disabled="!pending && !text.trim()"
        :aria-label="pending ? $t('sightline.stop') : $t('sightline.send')"
        @click="sendOrStop"
      >
        <SvgIcon v-if="pending" name="general/stop" width="18" height="18" />
        <SvgIcon v-else name="general/send" width="18" height="18" />
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";

.composer {
  background: colors.$background-grey-light;
  border: 1px solid colors.$border-light;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  transition: border-color 0.12s ease;

  &:focus-within {
    border-color: colors.$main;
  }

  .input-area {
    display: flex;

    textarea {
      @include typography.medium;

      flex: 1;
      border: none;
      outline: none;
      resize: none;
      background: transparent;
      color: colors.$text;
      line-height: 1.5;
      max-height: 140px;
      overflow-wrap: break-word;

      &::placeholder {
        @include typography.medium("regular");

        color: colors.$text-placeholder;
      }
    }
  }

  .bottom-row {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;

    .send {
      display: flex;
      outline: revert;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: 1px solid colors.$border-light;
      border-radius: 4px;
      background: colors.$background-ui;
      color: colors.$icon-subtle;
      cursor: pointer;
      flex-shrink: 0;

      &:hover {
        color: colors.$icon;
      }

      &:disabled {
        color: colors.$icon-disabled;
        cursor: default;
      }
    }
  }
}
</style>
